import axios from 'axios'
import { BASE_FPL_URL } from 'constants/api'
import type { Captain, Player, PlayerSummary } from 'types/fpl/elements'
import type { TeamSummary } from 'types/fpl/teams'
import type { Chip } from 'types/fpl/chips'
import type { LeagueTransferStats } from 'types/fpl/transfers'

const LEAGUE_ALL_TIMEOUT_MS = 120_000
const GW_PICKS_TIMEOUT_MS = 30_000

function leagueApiErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (error.code === 'ERR_CANCELED') {
      return 'Request canceled'
    }
    if (error.code === 'ECONNABORTED') {
      return 'Request timed out. The server may be busy or unavailable.'
    }
    if (!error.response) {
      return 'Could not reach the server. Check that the backend is running.'
    }
    const detail = error.response.data?.detail
    if (typeof detail === 'string' && detail.trim()) {
      return detail
    }
    return `Server error (${error.response.status})`
  }
  if (error instanceof Error && error.message) {
    return error.message
  }
  return 'Failed to fetch league data'
}

export const isLeagueRequestCanceled = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.code === 'ERR_CANCELED'

export type InterestVerdict = 'boring' | 'ok' | 'interesting' | 'spicy'

export interface InterestScore {
  captain_diversity: number
  chips_usability: number
  differentials_captains: number
  /** @deprecated no longer returned — calendar context lives in AI summary only */
  gameweek_stakes?: number
  /** Transfer market heat: volume, rolled FTs, paid minuses (0–10) */
  transfer_activity?: number
  total_interest_score: number
  summary: string
  verdict: InterestVerdict
}

export interface LeagueTemplatePlayerRow {
  player: Player
  starts_league_count: number
  starts_league_pct: number
}

export interface LeagueTemplate {
  formation: string
  players: LeagueTemplatePlayerRow[]
  /** Sum of league XI start counts for the 11 chosen players */
  template_start_sum: number
}

export interface TemplatinessStats {
  /** Mean pairwise Jaccard on starting XI sets (0–1) */
  xi_jaccard_avg_league: number
  xi_jaccard_avg_top10: number
}

export interface LeagueAllResult {
  captains: Captain[]
  chips: Chip[]
  transfers: LeagueTransferStats
  interest: InterestScore | null
  /** Teams in the league (from standings) */
  total_teams?: number
  /** Sum of all captain multipliers in the league (for effective captain share) */
  captain_multiplier_total?: number
  /** True if viewer_entry is one of the teams in this league (for importance n-1 self-exclusion) */
  viewer_in_league?: boolean
  league_template?: LeagueTemplate | null
  templatiness?: TemplatinessStats | null
}

export interface GwPickRow {
  element: number
  multiplier: number
}

export const gwPicksToMultiplierMap = (rows: GwPickRow[]): Record<number, number> =>
  Object.fromEntries(rows.map((r) => [r.element, r.multiplier]))

export const getGwUserPicks = async (
  entryId: number,
  gwId: number,
  signal?: AbortSignal,
): Promise<GwPickRow[]> => {
  try {
    const response = await axios.get<{ picks: GwPickRow[] }>(
      `${BASE_FPL_URL}/picks/${entryId}/${gwId}`,
      { timeout: GW_PICKS_TIMEOUT_MS, signal },
    )
    return response.data.picks ?? []
  } catch (error) {
    if (isLeagueRequestCanceled(error)) throw error
    return []
  }
}

export const getLeagueAll = async (
    leagueID: number,
    gwID: number,
    excludedIds: number[] = [],
    excludedTeamIds: number[] = [],
    viewerEntryId?: number,
    signal?: AbortSignal,
): Promise<LeagueAllResult> => {
    const params = new URLSearchParams()
    excludedIds.forEach(id => params.append('excluded', String(id)))
    excludedTeamIds.forEach(id => params.append('excluded_teams', String(id)))
    if (viewerEntryId != null) {
        params.append('viewer_entry', String(viewerEntryId))
    }
    const queryStr = params.toString()
    const url = `${BASE_FPL_URL}/all/${leagueID}/${gwID}${queryStr ? `?${queryStr}` : ''}`
    try {
        const response = await axios.get<LeagueAllResult>(url, {
          timeout: LEAGUE_ALL_TIMEOUT_MS,
          signal,
        })
        const data = response.data
        if (!data || !Array.isArray(data.captains) || !Array.isArray(data.chips)) {
            throw new Error('Invalid response from server')
        }
        return data
    } catch (error) {
        if (isLeagueRequestCanceled(error)) throw error
        console.error('getLeagueAll failed:', error)
        throw new Error(leagueApiErrorMessage(error))
    }
}


export const getLeagueCaptains = async (
    leagueID: number,
    gwID: number,
    excludedIds: number[] = [],
    excludedTeamIds: number[] = [],
) => {
    try {
        const params = new URLSearchParams()
        excludedIds.forEach(id => params.append('excluded', String(id)))
        excludedTeamIds.forEach(id => params.append('excluded_teams', String(id)))
        const queryStr = params.toString()
        const url = `${BASE_FPL_URL}/captains/${leagueID}/${gwID}${queryStr ? `?${queryStr}` : ''}`
        let response = await axios.get(url);
        return response.data
    } catch (error) {
        return console.log(error);
    }
}


export const getLeagueChips = async (leagueID: number, gwID: number) => {
    try {
        let response = await axios.get(`${BASE_FPL_URL}/chips/${leagueID}/${gwID}`);
        return response.data
    } catch (error) {
        return console.log(error);
    }
}


export const getLeagueInsights = async (leagueID: number, gwID: number) => {
    try {
        let response = await axios.get(`${BASE_FPL_URL}/insights/${leagueID}/${gwID}`);
        return response.data
    } catch (error) {
        return console.log(error);
    }
}

export const getLeagueTransferStats = async (leagueID: number, gwID: number) => {
    try {
        const response = await axios.get(`${BASE_FPL_URL}/transfers/${leagueID}/${gwID}`)
        return response.data
    } catch (error) {
        return console.log(error)
    }
}


export const getPlayers = async (): Promise<PlayerSummary[]> => {
    try {
        let response = await axios.get(`${BASE_FPL_URL}/players`);
        return response.data
    } catch (error) {
        console.log(error);
        return []
    }
}

export const getTeams = async (): Promise<TeamSummary[]> => {
    try {
        let response = await axios.get(`${BASE_FPL_URL}/teams`);
        return response.data
    } catch (error) {
        console.log(error);
        return []
    }
}

